import { AfterViewInit, Component, ElementRef, Input, OnInit, OnDestroy } from '@angular/core';
import { InfinityUtil }             from '../../infinity-util.service';
import { bindAll }                  from "../../infinity-util/methods/bindAll";
import { readFileAsync }            from "../../infinity-dom/methods/readFileAsync";
import { environment }              from '../../../environments/environment';

interface TableCellRadio {
    id: string | null;
    name: string | null;
    checked: boolean;
    disabled: boolean;
}

interface TableCellViewModel {
    className: string | null;
    colSpan: string | null;
    rowSpan: string | null;
    style: string | null;
    html: string;
    radio: TableCellRadio | null;
}

@Component({
    selector: 'infinity-item',
    templateUrl: './infinity-item.component.html',
    styleUrls: ['./infinity-item.component.scss']
})
export class InfinityItemComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() item: any;
    @Input() parent?: any;
    @Input() actions: any;

    // Store display variables.
    public is: any = {};
    public className: string;
    public displayId: string;
    public mask: string;
    public placeholder: string;

    public updateTimeout: any;
    public maxSlashes: number;
    public maxDigits: number;
    public maxLast: number;
    public cardConnectIframeSrc: string;
    private cardConnectUrl: URL;
    private readonly tableCellCache: Map<string, TableCellViewModel[]> = new Map<string, TableCellViewModel[]>();

    constructor(public util: InfinityUtil, public el: ElementRef<HTMLElement>) { bindAll(this);
    }

    ngOnInit() {
        this.is[this.item.type] = true;
        if (!this.item.itemId) {
            this.util.set({ uid: this.util.get("uid", null, 0) + 1 });
            this.item.itemId = "uid" + this.util.store.uid;
        }
        this.className = `infinity-${this.item.type} ${this.item.className || ""}`;
        this.displayId = (parseInt(this.item.itemId) ? "i" : "") + this.item.itemId;
        this.mask = this.item.mask;
        this.placeholder = this.item.placeholder || (this.is.date ? "mm/dd/yyyy" : "");
        this.maxSlashes = (this.item.data || {}).maxSlashes;
        this.maxDigits = (this.item.data || {}).maxDigits;
        this.maxLast = (this.item.data || {}).maxLast;
        
        // for credit card component
        if (this.is.cc) {
            this.cardConnectUrl = new URL(`${environment.cardPointeUrl}`);
            this.cardConnectIframeSrc = this.toSafeCardConnectIframeSrc(this.getCardConnectIframeSrc());
        }        

        setTimeout(() => {
            // Watch for item updates.
            if (this.item.itemId && !this.is.attachment) {
                this.util.watch(this.item.itemId, item => this.updateFromStore(item, this.util.get(["value", "className", "label"], item)), { backfill: true });
            }
            // Watch for table row updates.
            if (this.is.table) {
                this.item.items.forEach(child => {
                    if (child.itemId) this.util.watch(child.itemId, child => this.updateFromStore(child, { label: child.label }), { backfill: true });
                });
                this.initTableScrollbar();
            }
            // Watch for dropdown selection visibility.
            if (this.is.dropdown) {
                this.item.items.forEach(child => {
                    if (child.itemId) this.util.watch(child.itemId, child => {
                        if (child.visible || this.item.value !== child.label) return;
                        this.item.value = "";
                        this.util.set({ [this.item.itemId]: this.item }, { batch: true });
                    });
                });
            }
        }, 0);
    }

    ngAfterViewInit() {
        // Add event listener when the credit card number control is rendered
        if (this.is.cc) {
            this.setCardConnectIframeSrc();
            window.addEventListener('message', this.cardConnectMessageListener);
        }
    }
    
    ngOnDestroy() {
        if (this.is.cc) {
            window.removeEventListener('message', this.cardConnectMessageListener);
        }
    }

    private readonly cardConnectMessageListener = (event: MessageEvent) => {
        if (event.origin !== this.cardConnectUrl.origin) return;
        let messageData: any;
        if (typeof event.data === "string") {
            try {
                messageData = JSON.parse(event.data);
            } catch {
                return;
            }
        } else if (event.data && typeof event.data === "object") {
            messageData = event.data;
        } else {
            return;
        }
        
        const ccNumToken = this.util.get("creditCardNumber");
        const ccNumInput = this.util.get("creditCardNumberInput");
        
        if (messageData.validationError && ccNumInput.valid === true) {
            ccNumToken.value = "";
            ccNumInput.valid = false;
        
        } else if (messageData.message && ccNumToken.value !== messageData.message) {
            ccNumToken.value = messageData.message;
            ccNumInput.valid = true;
        }
        
        this.devLog(
            "CC tokenization result: ",
            messageData.message ? messageData.message : messageData.validationError);
    }

    updateFromStore(item: any, updates: any) {
        const { className, label, value, required } = updates;
        if (className !== undefined) this.className = `infinity-${item.type} ${className || ""}`;
        if (label !== undefined) item.label = label;
        if (required !== undefined) item.required = required;
        if (value === undefined && this.is.radioGroup) {
            const inputs = document.getElementsByTagName("input");
            for (let i = 0, l = inputs.length; i < l; i++) {
                if (inputs[i].name === item.itemId) return inputs[i].checked = false;
            }
        }
        if (value !== undefined) {
            // Get html element from item id.
            const displayId = (parseInt(item.itemId) ? "i" : "") + item.itemId;
            const element: any = document.getElementById(displayId);

            // Dropdown selection.
            const options: Array<any> = this.util.get("options", element, []);
            if (this.is.dropdown && options.length) {
                for (var i = 0; i < options.length; i++) {
                    if (options[i].value === value) return options[i].selected = true;
                }
            }

            // Checkbox selection.
            if (this.is.checkbox) element.checked = value;

            // Radio group selection / unselection.
            if (this.is.radioGroup && value) {
                const selection: any = document.getElementById(value);
                if (selection) return selection.checked = true;
            }
            if (this.is.radioGroup && !value) {
                item.items.forEach(child => {
                    const selectionId = (parseInt(child.itemId) ? "i" : "") + child.itemId;
                    const selection: any = document.getElementById(selectionId);
                    if (selection) selection.checked = false;
                });
            }

            // Set html input value.
            if (element) {
                element.value = value;
                element.setAttribute("value", value);
            }
        }
    }

    updateFromInput(item: any, key: string, value: any) {
        item[key] = value;
        this.util.set({ [item.itemId]: item }, { batch: true });
    }

    getTableHeaderCells(label: any): TableCellViewModel[] {
        return this.parseTableCells(label, "th");
    }

    getTableRowCells(label: any): TableCellViewModel[] {
        return this.parseTableCells(label, "td");
    }

    selectTableRow(item: any, row: any, event: Event): void {
        const target = event.target as HTMLElement;
        if (target && this.isTableCellControl(target)) {
            return;
        }

        if (!this.isTableRowSelectable(row.label)) {
            return;
        }

        this.updateFromInput(item, "value", row.itemId);
    }

    isTableRowSelectable(label: any): boolean {
        const cells = this.getTableRowCells(label);
        for (const cell of cells) {
            if (cell.radio) {
                return cell.radio.disabled !== true;
            }
        }
        return true;
    }

    private isTableCellControl(target: HTMLElement): boolean {
        return !!target.closest("input, button, a, select, textarea, label");
    }

    private parseTableCells(rawHtml: any, cellTag: "th" | "td"): TableCellViewModel[] {
        const html = String(rawHtml || "");
        const cacheKey = `${cellTag}:${html}`;
        const cached = this.tableCellCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const wrapper = document.createElement("template");
        const sectionTag = cellTag === "th" ? "thead" : "tbody";
        wrapper.innerHTML = `<table><${sectionTag}><tr>${html}</tr></${sectionTag}></table>`;

        const row = wrapper.content.querySelector("tr");
        if (!row) {
            const emptyResult: TableCellViewModel[] = [];
            this.tableCellCache.set(cacheKey, emptyResult);
            return emptyResult;
        }

        const cells = Array.from(row.children).filter(node => node.nodeName.toLowerCase() === cellTag) as HTMLElement[];
        const parsed = cells.map(cell => {
            const cellClone = cell.cloneNode(true) as HTMLElement;
            const radio = cellClone.querySelector<HTMLInputElement>("input[type='radio']");
            let radioConfig: TableCellRadio | null = null;

            if (radio) {
                radioConfig = {
                    id: radio.getAttribute("id"),
                    name: radio.getAttribute("name"),
                    checked: radio.checked || radio.hasAttribute("checked"),
                    disabled: radio.disabled || radio.hasAttribute("disabled")
                };
                radio.remove();
            }

            return {
                className: cell.getAttribute("class"),
                colSpan: cell.getAttribute("colspan"),
                rowSpan: cell.getAttribute("rowspan"),
                style: cell.getAttribute("style"),
                html: cellClone.innerHTML,
                radio: radioConfig
            };
        });

        this.tableCellCache.set(cacheKey, parsed);
        return parsed;
    }

    maskDate(event: any, maxIndex: number = 2, maxDigits: number = 2, maxLast: number = 4) {
        const alwaysAllowKeys = ["ArrowLeft", "ArrowRight", "Tab", "Backspace", "Delete", "Alt", "Shift", "Control"];
        const onlyAllowKeys = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "/"];

        if (this.updateTimeout) clearTimeout(this.updateTimeout);
        if (alwaysAllowKeys.indexOf(event.key) > -1) return;                        // Always allow these keys.
        if (onlyAllowKeys.indexOf(event.key) === -1) return event.preventDefault(); // Then only allow numbers and slashes.

        const value = event.target.value || "";
        const split = value.split("/");

        const cursor = event.target.selectionStart;
        var section = 0;
        for (let i = 0; i < cursor; i++) {
            if (value[i] === "/") section += 1;
        }

        const sectionValue = split[section];
        const nextSectionValue = split[section + 1];

        if (!sectionValue.length && event.key === "/") return event.preventDefault();   // Don't allow slashes as the first digit in a section.

        if (section !== maxIndex) {                                                     // If section is not the last section:
            if (nextSectionValue && event.key === "/") return event.preventDefault();   // If next section exists, dont allow slash.
            if (!nextSectionValue && event.key === "/") return;                         // If next section doesn't exist, allow default slash.
            if (sectionValue.length !== maxDigits) return;                              // If section isn't full, allow typing.
            if (!nextSectionValue) event.target.value += "/";                           // If section is full and no next section exists, add slash.
            else event.preventDefault();                                                // If section full and next section exists, prevent typing.
        }
        else {                                                                                  // If section is the last section:
            if (sectionValue.length === maxLast || event.key === "/") event.preventDefault();   // If section is full or key is slash, prevent typing.
        }
    }

    queueUpdate(event: any) {
        if (this.updateTimeout) clearTimeout(this.updateTimeout);
        this.updateTimeout = setTimeout(() => this.updateFromInput(this.item, "value", event.target.value), 500);
    }

    async updateFromFile(files: Array<any>) {
        const encodedBinary = await readFileAsync(files[0]);                                // Get encoded binary.
        this.updateFromInput(this.item, "value", { name: files[0].name, encodedBinary });   // Set name and encodedBinary as value on item.
    }

    initTableScrollbar() {
        if (document.body.clientWidth > 465) return;
        if (this.className.indexOf("big-table") < 0) return;
        
        var root = this.el.nativeElement;

        /* --- Contants --- */

        const scrollbar = root.getElementsByClassName("scroll-bar")[0];
        const contentContainer = root.getElementsByClassName("table-container")[0];
        const containerWidth = contentContainer.clientWidth;
        const contentWidth = contentContainer.scrollWidth;
        const scrollLength = contentWidth - containerWidth;
        const scrollbarLength = Math.floor((containerWidth / contentWidth) * containerWidth);
        const availableScrollLength = containerWidth - scrollbarLength;

        /* --- Set Scrollbar Width --- */

        setStyleProp(scrollbar, "width", scrollbarLength + "px");

        function setStyleProp(element, setKey, setValue) {
            var currentStyle = element.getAttribute("style") || "";
            var props = currentStyle.split("; ").filter(e => e);
            if (currentStyle.indexOf(`${setKey}: `) < 0) props.push(`${setKey}: ${setValue}; `);
            var style = props.reduce((str, prop) => {
                var [key, value] = prop.split(": ");
                if (key !== setKey) str += `${key}: ${value}; `;
                else str += `${key}: ${setValue}; `;
                return str;
            }, "");
            element.setAttribute("style", style);
        }

        /* --- Scrollbar Click Scrolling --- */

        var scrolling = false;
        var scrollValue = 0;
        var moveSpeed = 4;

        scrollbar.addEventListener("mousedown", e => {
            scrolling = true;
            scrollbar.classList.add("scrolling");
        });

        document.addEventListener("mouseup", e => {
            scrolling = false;
            scrollbar.classList.remove("scrolling");
        });

        document.addEventListener("mousemove", e => {
            if (!scrolling) return;
            if (e.movementX < 0) { // Moving Left
                // Get scroll value.
                if (scrollValue === 0) return;
                scrollValue -= moveSpeed;
                if (scrollValue < 0) scrollValue = 0;

                // Scroll scrollbar.
                setStyleProp(scrollbar, "transform", `translate3d(${scrollValue}px, 0px, 0px)`);

                // Scroll container.
                var containerValue = (scrollValue / availableScrollLength) * scrollLength;
                contentContainer.scroll(containerValue, 0);
            }
            if (e.movementX > 0) { // Moving Right
                // Get scroll value.
                if (scrollValue === availableScrollLength) return;
                scrollValue += moveSpeed;
                if (scrollValue > availableScrollLength) scrollValue = scrollbarLength;

                // Scroll scrollbar.
                setStyleProp(scrollbar, "transform", `translate3d(${scrollValue}px, 0px, 0px)`);

                // Scroll container.
                var containerValue = (scrollValue / availableScrollLength) * scrollLength;
                contentContainer.scroll(containerValue, 0);
            }
        });

        /* --- Scrollbar Touch Scrolling --- */

        const baseX = (document.body.clientWidth - containerWidth) / 2;

        this.devLog("baseX", baseX);

        var touchX = baseX;
        var leftEdge = 0;
        var rightEdge = scrollbarLength;

        scrollbar.addEventListener("touchstart", (e: TouchEvent) => {
            scrolling = true;
            touchX = baseX + e.changedTouches[0].clientX;
            scrollbar.classList.add("scrolling");
        });

        scrollbar.addEventListener("touchend", (e: TouchEvent) => {
            scrolling = false;
            scrollbar.classList.remove("scrolling");
        });

        scrollbar.addEventListener("touchmove", (e: TouchEvent) => {
            if (!scrolling) return;
            var newTouchX = baseX + e.changedTouches[0].clientX;
            var diff = newTouchX - touchX;

            if (diff < 0) { // Moving Left
                // Get Left Edge
                leftEdge += diff;
                if (leftEdge < 0) leftEdge = 0;
                rightEdge = leftEdge + scrollbarLength;
            }

            if (diff > 0) { // Moving Right
                // Get Right Edge
                rightEdge += diff;
                if (rightEdge > containerWidth) rightEdge = containerWidth;
                leftEdge = rightEdge - scrollbarLength + baseX;
            }

            // Scroll scrollbar.
            setStyleProp(scrollbar, "transform", `translate3d(${leftEdge}px, 0px, 0px)`);

            // Scroll container.
            var containerValue = (leftEdge / availableScrollLength) * scrollLength;
            contentContainer.scroll(containerValue, 0);

            // Set new position.
            touchX = newTouchX;
        });

        /* --- Container Scrolling --- */

        contentContainer.addEventListener("scroll", e => {
            if (scrolling) return;

            // Get scroll value.
            scrollValue = (contentContainer.scrollLeft / scrollLength) * availableScrollLength;

            // Scroll scrollbar.
            setStyleProp(scrollbar, "transform", `translate3d(${scrollValue}px, 0, 0)`);
        });
    }

    // Returns a URL for CardConnect iframe with settings
    getCardConnectIframeSrc(): string {
        // Custom CSS for iframe content
        const css = `
            body { margin: 0; }
            input {
                box-sizing: border-box;
                height: 34px !important;
                width: 100% !important;
                border: 1px solid rgb(118,118,118) !important;
                font-family: 'Raleway Lining', Arial, sans-serif;
                font-size: 14px !important;
                padding: 6px !important;
            }
        `.replace(/\n/g, '').trim();

        const url = new URL(this.cardConnectUrl.toString());
        [
            ["invalidinputevent", "true"],
            ["formatinput", "true"],
            ["cardnumbernumericonly", "true"],
            ["cardinputmaxlength", "19"],
            ["css", css]
        ].forEach(([key, value]) => url.searchParams.set(key, value));
        
        return url.toString();
    }

    private toSafeCardConnectIframeSrc(rawUrl: string): string {
        try {
            const iframeUrl = new URL(rawUrl);
            const isHttps = iframeUrl.protocol === "https:";
            if (isHttps && iframeUrl.origin === this.cardConnectUrl.origin) {
                return iframeUrl.toString();
            }
        } catch {
            // no-op
        }

        this.devLog("Blocked unsafe CardConnect iframe URL", rawUrl);
        return "about:blank";
    }

    private setCardConnectIframeSrc(): void {
        const root = this.el.nativeElement;
        const iframe = root.querySelector<HTMLIFrameElement>("#tokenframe");
        if (!iframe) {
            return;
        }
        iframe.src = this.cardConnectIframeSrc || "about:blank";
    }

    private readonly devLog = (...args: any[]) => { !environment.production && console.log(...args); };
}
